import * as React from 'react';
import { type JSX, Suspense } from 'react';
import {
    Button,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    useRestoreFocusSource,
} from '@fluentui/react-components';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import actions from './aktionen.md?raw';
import processes from './prozesse.md?raw';
import juelDocumentation from '../documentation/juel.md?raw';
import juelExtensionsDocumentation from '../documentation/juel-extensions.md?raw';
import spJsonDocumentation from '../documentation/sp-json-schema.md?raw';
import spOdataDocumentation from '../documentation/sp-odata-filter.md?raw';
import booleanUtil from '../documentation/booleanUtil.md?raw';
import conversionUtil from '../documentation/conversionUtil.md?raw';
import collectionUtil from '../documentation/collectionUtil.md?raw';
import dateUtil from '../documentation/dateUtil.md?raw';
import jsonUtil from '../documentation/jsonUtil.md?raw';
import mathUtil from '../documentation/mathUtil.md?raw';
import stringUtil from '../documentation/stringUtil.md?raw';
import {
    ArchiveRegular,
    ArrowExportRegular,
    ArrowImportRegular,
    ArrowResetRegular,
    Dismiss24Regular,
    DocumentAddRegular,
    DocumentCheckmarkRegular,
    DocumentCopyRegular,
    DocumentDismissRegular,
    DocumentProhibitedRegular,
    DocumentSyncRegular,
} from '@fluentui/react-icons';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'github-markdown-css/github-markdown.css';

type TMarkdownControlProps = {
    docType:
        | 'juel'
        | 'juel-extensions'
        | 'sp-json'
        | 'sp-odata'
        | 'actions'
        | 'processes'
        | 'booleanUtil'
        | 'collectionUtil'
        | 'conversionUtil'
        | 'dateUtil'
        | 'jsonUtil'
        | 'mathUtil'
        | 'stringUtil';
    open: boolean;
    setOpen: (open: boolean) => void;
};

export const DocPanel = (props: TMarkdownControlProps): JSX.Element | null => {
    const restoreFocusSourceAttributes = useRestoreFocusSource();

    let documentationContent: string;
    switch (props.docType) {
        case 'juel':
            documentationContent = juelDocumentation;
            break;
        case 'juel-extensions':
            documentationContent = juelExtensionsDocumentation;
            break;
        case 'sp-json':
            documentationContent = spJsonDocumentation;
            break;
        case 'sp-odata':
            documentationContent = spOdataDocumentation;
            break;
        case 'actions':
            documentationContent = actions;
            break;
        case 'processes':
            documentationContent = processes;
            break;
        case 'booleanUtil':
            documentationContent = booleanUtil;
            break;
        case 'collectionUtil':
            documentationContent = collectionUtil;
            break;
        case 'conversionUtil':
            documentationContent = conversionUtil;
            break;
        case 'dateUtil':
            documentationContent = dateUtil;
            break;
        case 'jsonUtil':
            documentationContent = jsonUtil;
            break;
        case 'mathUtil':
            documentationContent = mathUtil;
            break;
        case 'stringUtil':
            documentationContent = stringUtil;
            break;
        default:
            documentationContent = 'Dokumentation nicht gefunden.';
    }

    const iconMap: Record<string, React.ReactNode> = {
        Add: <DocumentAddRegular />,
        Publish: <DocumentCheckmarkRegular />,
        Copy: <DocumentCopyRegular />,
        Delete: <DocumentDismissRegular />,
        Import: <ArrowImportRegular />,
        Export: <ArrowExportRegular />,
        Deactivate: <DocumentProhibitedRegular />,
        Reactivate: <DocumentSyncRegular />,
        Reset: <ArrowResetRegular />,
        Archive: <ArchiveRegular />,
        Template: <DocumentAddRegular />,
    };

    function replaceIcons(children: React.ReactNode): React.ReactNode {
        return React.Children.map(children, (child) => {
            if (typeof child !== 'string') {
                return child;
            }

            const regex = /\[icon:(.*?)\]/g;
            const parts = child.split(regex);

            return parts.map((part, index) => {
                const icon = iconMap[part];
                if (icon) {
                    return (
                        <span key={index} style={{ marginLeft: 6, verticalAlign: 'text-bottom' }}>
                            {icon}
                        </span>
                    );
                }
                return part;
            });
        });
    }

    return (
        <Drawer
            position="end"
            as="aside"
            {...restoreFocusSourceAttributes}
            size="large"
            open={props.open}
            onOpenChange={(_, { open }) => props.setOpen(open)}
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close"
                            icon={<Dismiss24Regular />}
                            onClick={() => props.setOpen(false)}
                        />
                    }
                ></DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <Suspense>
                    <div className="markdown-body">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw, rehypeHighlight]}
                            components={{
                                p({ children }) {
                                    return <p>{replaceIcons(children)}</p>;
                                },
                                h1({ children }) {
                                    return <h1>{replaceIcons(children)}</h1>;
                                },
                                h2({ children }) {
                                    return <h2>{replaceIcons(children)}</h2>;
                                },
                                h3({ children }) {
                                    return <h3>{replaceIcons(children)}</h3>;
                                },
                            }}
                        >
                            {documentationContent}
                        </ReactMarkdown>
                    </div>
                </Suspense>
            </DrawerBody>
        </Drawer>
    );
};
